import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { faClipboard as faClipboardSolid } from '@fortawesome/free-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ZonedDateTime } from '@js-joda/core';
import { Box, ClickAwayListener, Avatar as MUIAvatar } from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import ExosAvatar from '../../assets/images/ExosAvatar.png';
import { MemberNotesDocument, NoteBasicFragment } from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import { BORDER_GRAY, COOL_GRAY700, WHITE } from '../../theme';
import { getTimeSince } from '../../utils/date';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
  ScreenNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Avatar from '../reusable/Avatar';
import { ButtonSize } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import { FloatingButton } from '../reusable/FloatingButton';
import LoadMore from '../reusable/LoadMore';
import PaginationWaypoint from '../reusable/PaginationWaypoint';
import Text, { TextColor, TextKind } from '../reusable/Text';
import Tooltip from '../reusable/Tooltip';
import { useNotesContext } from './Context';
import { Note } from './Note';
import NotesGuidelinesModal from './NotesGuidelineModal';

const StyledNotesContainer = styled.div<{
  $open?: boolean;
}>`
  display: flex;
  flex-direction: column;

  position: fixed;
  z-index: 3;

  left: 24px;
  bottom: 88px; // Distance of padding between and height of button

  width: 320px;
  min-height: 400px;
  max-height: calc(100vh - 150px);

  border-radius: 8px;
  background: ${WHITE};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  border: 1px solid ${BORDER_GRAY};

  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(12px)')};
  opacity: ${({ $open }) => ($open ? '1' : '0')};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};

  transition:
    transform 0.25s ease-in-out,
    visibility 0.25s ease-in-out,
    opacity 0.25s ease-in-out;
`;

const StyledNotesHeader = styled.header`
  align-items: center;
  display: flex;
  padding: 16px 16px 0;
  margin-bottom: 12px;
  gap: 8px;
  justify-content: space-between;
`;

const StyledNoNotesContainer = styled.div`
  flex: 1;
  flex-direction: column;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledNote = styled.span`
  display: flex;
  flex-direction: row;
  padding: 8px 0;
  margin: 0 16px;
  border-bottom: 1px solid ${BORDER_GRAY};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
`;

const StyledNoteContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 0 0;
`;

const StyledNoteImage = styled.div`
  margin-right: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;

const NoNotes = () => (
  <StyledNoNotesContainer>
    <FontAwesomeIcon
      data-testid="notes-no-notes-icon"
      icon={faClipboardSolid}
      color={COOL_GRAY700}
      fontSize={20}
      style={{ marginBottom: '16px' }}
    />
    <Text kind={TextKind.TitleXs} style={{ color: COOL_GRAY700 }}>
      No notes yet
    </Text>
    <Text
      kind={TextKind.BodyS}
      style={{ color: COOL_GRAY700, lineHeight: '20px' }}
    >
      Create your first note.
    </Text>
  </StyledNoNotesContainer>
);

const NoCoachAvatar = () => (
  <MUIAvatar
    sx={{
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: BORDER_GRAY,
      borderWidth: 0,
      borderStyle: 'solid',
    }}
    data-testid="noCoachAvatar"
  >
    <img alt="Imported Notes" src={ExosAvatar} />
  </MUIAvatar>
);

interface NotesViewProps {
  memberId: string;
  notes: NoteBasicFragment[];
  fetchMore: () => void;
  loading: boolean;
  loadingMore: boolean;
  hasNextPage: boolean;
}

const NoteItem: React.FC<{
  note: NoteBasicFragment;
  onClick: () => void;
}> = ({ note, onClick }) => (
  <StyledNote key={note.id} onClick={onClick} data-testid={`note-${note.id}`}>
    <StyledNoteImage>
      {note.coach ? (
        <Tooltip
          content={`${note.coach.firstName} ${note.coach.lastName}`}
          placement="top"
        >
          <Avatar size={48} user={note.coach} />
        </Tooltip>
      ) : (
        <NoCoachAvatar />
      )}
    </StyledNoteImage>

    <StyledNoteContent>
      <Text kind={TextKind.LabelL}>{note.title}</Text>
      <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
        {getTimeSince(ZonedDateTime.parse(note.updatedAt).toLocalDate())}
      </Text>
    </StyledNoteContent>
  </StyledNote>
);

const NotesView: React.FC<NotesViewProps> = ({
  memberId,
  notes,
  hasNextPage,
  loadingMore,
  loading,
  fetchMore,
}) => {
  const { track } = useTracking();
  const { notesListOpen, setNotesListOpen, openNote } = useNotesContext();

  const handleOpenNote = (noteId: string) => {
    track(EventNames.NotesListClick, {
      note_id: noteId,
      member_id: memberId,
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.NotesList,
      screen_name: ScreenNames.MemberProfile,
    });
    openNote({ noteId });
  };

  const handleCreateNote = () => {
    track(EventNames.NotesListCreateClick, {
      member_id: memberId,
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.NotesList,
      screen_name: ScreenNames.MemberProfile,
    });
    openNote();
  };

  return (
    <ClickAwayListener onClickAway={() => setNotesListOpen(false)}>
      <Box>
        <StyledNotesContainer
          $open={notesListOpen}
          data-testid="notes-container"
        >
          <StyledNotesHeader>
            <Text kind={TextKind.TitleS}>Notes</Text>
            <IconButton
              testId="notes-header-create-button"
              size={ButtonSize.Small}
              icon={<FontAwesomeIcon icon={faPlus} fontSize={20} />}
              onClick={handleCreateNote}
            />
          </StyledNotesHeader>
          {loading && <LoadingPanel height={150} noBorder />}
          {!loading && (
            <div
              style={{
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {notes.length > 0 ? (
                <>
                  {notes.map((note) => (
                    <NoteItem
                      key={note.id}
                      note={note}
                      onClick={() => handleOpenNote(note.id)}
                    />
                  ))}
                  <PaginationContainer>
                    <PaginationWaypoint callback={fetchMore} />
                    {!loading && hasNextPage && (
                      <LoadMore
                        loadMore={fetchMore}
                        isFetchingMore={loadingMore}
                      />
                    )}
                  </PaginationContainer>
                </>
              ) : (
                <NoNotes />
              )}
            </div>
          )}
        </StyledNotesContainer>

        <FloatingButton
          testId="FloatingNotesButton"
          open={notesListOpen}
          onClick={() => setNotesListOpen(!notesListOpen)}
          icon={faClipboard}
          position="left"
          trackingOpenEvent={EventNames.NotesIconClick}
          trackingCategory={EventCategories.Notes}
          trackingSubCategory={EventSubCategories.NotesList}
          trackingMemberId={memberId}
        />
      </Box>
    </ClickAwayListener>
  );
};

interface NotesProps {
  memberId: string;
}

export const Notes: React.FC<NotesProps> = ({ memberId }) => {
  const { track } = useTracking();

  const { data, fetchMore, networkStatus } = useQuery(MemberNotesDocument, {
    variables: { id: memberId, input: { first: 15 } },
    notifyOnNetworkStatusChange: true,
  });

  const { edges, pageInfo } = data?.user?.notes ?? {
    edges: [],
    pageInfo: { endCursor: '', hasNextPage: false },
  };
  const notes = edges.map((edge) => edge.node);

  const { endCursor, hasNextPage } = pageInfo;
  const loading = networkStatus === NetworkStatus.loading;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const fetchMoreNotes = () => {
    if (loadingMore || !hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        id: memberId,
        input: { first: 15, after: endCursor },
      },
    });
  };

  const { guidelinesOpen, setGuidelinesOpen, noteId } = useNotesContext();
  const onGuidelinesClose = () => {
    track(EventNames.CloseModal, {
      member_id: memberId,
      event_category: EventCategories.Notes,
      event_subcategory: EventSubCategories.Information,
      note_id: noteId ?? undefined,
    });
    setGuidelinesOpen(false);
  };
  return (
    <>
      <NotesView
        memberId={memberId}
        notes={notes}
        fetchMore={fetchMoreNotes}
        loading={loading}
        loadingMore={loadingMore}
        hasNextPage={hasNextPage}
      />
      <Note />
      <NotesGuidelinesModal
        isOpen={guidelinesOpen}
        onClose={onGuidelinesClose}
      />
    </>
  );
};
